import { axiosInstance } from "./axios";

export const exportMatchesKpisToExcel = async (
    matchIds: number[],
    teamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const response = await axiosInstance.post<Blob>(
        "/matches/kpis/excel",
        { matchIds, teamId },
        {
            responseType: "blob",
        }
    );

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportTournamentAnalysisToExcel = async (
    tournamentId: number,
    teamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const requestBody = tournamentId === 0 ? {} : { tournamentId };
    const response = await axiosInstance.post<Blob>(`/tournament/analysis/excel/${teamId}`, requestBody, {
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportPlayerComparisonToExcel = async (
    tournamentId: number,
    player1Id: number,
    player2Id: number,
    teamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const requestBody = {
        tournamentId,
        player1Id,
        player2Id,
        teamId,
    };

    const response = await axiosInstance.post<Blob>("/players/compare/excel", requestBody, {
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportPlayerAnalysisToExcel = async (
    playerId: number,
    tournamentId?: number,
    teamId?: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const requestBody = { playerId, ...(tournamentId ? { tournamentId } : {}), ...(teamId ? { teamId } : {}) };

    const response = await axiosInstance.post<Blob>("/player/analysis/excel", requestBody, {
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportPlayerComparisonByMatchesToExcel = async (
    matchIds: number[],
    player1Id: number,
    player1TeamId: number,
    player2Id: number,
    player2TeamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const requestBody = {
        matchIds,
        player1Id,
        player1TeamId,
        player2Id,
        player2TeamId,
    };

    const response = await axiosInstance.post<Blob>("/players/compare/matches/excel", requestBody, {
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportScoutingPlayersToExcel = async (
    tournamentId: number,
    filters: { search?: string; teams?: string[]; positions?: string[] },
    teamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const params = new URLSearchParams();
    params.append("teamId", String(teamId));
    if (filters.search) params.append("search", filters.search);
    filters.teams?.forEach((t) => params.append("teams", t));
    filters.positions?.forEach((p) => params.append("positions", p));

    const response = await axiosInstance.get<Blob>(`/${tournamentId}/scouting/players/excel`, {
        params,
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const exportScoutingGoalkeepersToExcel = async (
    tournamentId: number,
    filters: { search?: string; teams?: string[] },
    teamId: number
): Promise<{ data: Blob; contentDisposition?: string }> => {
    const params = new URLSearchParams();
    params.append("teamId", String(teamId));
    if (filters.search) params.append("search", filters.search);
    filters.teams?.forEach((t) => params.append("teams", t));

    const response = await axiosInstance.get<Blob>(`/${tournamentId}/scouting/goalkeepers/excel`, {
        params,
        responseType: "blob",
    });

    return {
        data: response.data,
        contentDisposition: response.headers["content-disposition"],
    };
};

export const downloadExcelFile = (blob: Blob, contentDisposition?: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    let filename = "";

    if (contentDisposition) {
        const patterns = [
            /filename\*=UTF-8''([^;]+)/i,
            /attachment;\s*filename=([^;,\s]+)/i,
            /filename\*=([^;]+)/i,
            /filename=([^;,\n]+)/i,
            /filename=['"]([^'"]+)['"]/i,
        ];

        for (const pattern of patterns) {
            const match = contentDisposition.match(pattern);
            if (match && match[1]) {
                filename = match[1].trim().replace(/['"]/g, "");
                if (filename.includes("%")) {
                    try {
                        const decoded = decodeURIComponent(filename);
                        filename = decoded;
                    } catch {
                        // Decoding failed, keep original filename
                    }
                }
                break;
            }
        }
    }

    if (!filename) {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 16).replace("T", "_").replace(/:/g, "-");
        filename = `Hockey_Tournament_Analysis_Export_${dateStr}.xlsx`;
    }
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};
